/**
 * chain-eos/rpc-fetch.ts — timeout-bounded RPC POST, ported from webdex's
 * `rpcFallback.ts` (___webdex/src/libs/api/rpcFallback.ts).
 *
 * The gap this closes: every EOS_RPC_ENDPOINTS loop in this package (accountExists,
 * fetchAvatarChoices, etc.) already tries the next endpoint on a thrown error, but a
 * plain `fetch()` never throws on its own for a slow/hanging node — it just waits.
 * One unresponsive endpoint at the front of the list can stall the whole chain of
 * fallbacks far longer than the user should ever wait. webdex already solved this
 * with an AbortController timeout per attempt; this is the same mechanism, reusable
 * across chain-eos instead of copy-pasted per call site.
 */

/** How long to wait for one RPC node before giving up on it and trying the next. */
export const RPC_TIMEOUT_MS = 6000

/**
 * POSTs `body` as JSON to `${baseUrl}${path}` with a hard timeout. Rate-limit
 * responses (420/429 — same codes webdex treats specially) are surfaced as a
 * thrown error too, so a caller's "try the next endpoint" loop treats a
 * rate-limited node the same as an unreachable one instead of parsing its body.
 */
export async function fetchRpc(
  baseUrl: string,
  path: string,
  body: unknown,
  timeoutMs: number = RPC_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    if (res.status === 420 || res.status === 429) {
      throw new Error(`rate limited (${res.status})`)
    }
    return res
  } finally {
    clearTimeout(timeout)
  }
}
